import React from 'react'
import { makeStyles } from '@mui/styles'
import { user } from 'ampersand-app'
import ThemeInterface from '@/theme-interface'
import lang from '@/lang'
import config from '@/config'
import request from '@/modules/base/util/request'
import { experimentalFeatures } from '@/modules/base/util/constants'
import { toast } from '@/modules/base/components/toast/toastConfig'

const useStyles = makeStyles((theme: ThemeInterface) => ({
  wrapper: {
    marginTop: theme.spacing(3),
    paddingTop: theme.spacing(2.5),
    borderTop: theme.border,
  },
  label: {
    color: theme.labelLight,
    fontSize: theme.fontSizes.smaller,
    fontWeight: theme.fontWeights.regular,
    marginBottom: theme.spacing(1.25),
    letterSpacing: '0.12px',
    display: 'block',
    textTransform: 'uppercase',
  },
  value: {
    color: theme.black,
    fontSize: theme.fontSizes.default,
  },
  action: {
    marginTop: theme.spacing(1.5),
  },
  button: {
    background: 'none',
    border: `1px solid ${theme.borderColor}`,
    color: theme.black,
    borderRadius: theme.borderRadius,
    padding: '6px 12px',
    fontSize: theme.fontSizes.small,
    cursor: 'pointer',
    '&:disabled': {
      opacity: 0.5,
      cursor: 'default',
    },
  },
  running: {
    color: theme.labelLight,
    fontSize: theme.fontSizes.small,
    fontStyle: 'italic',
  },
}))

// FLAG:useDocuAi
// Surfaces DocuAI's embedded-image OCR stats for office docs in the document
// drawer, and offers an "OCR ausführen" trigger that re-runs the document with
// forced embedded-image OCR. New code introduced entirely behind useDocuAi:
// renders nothing when the flag is off (baseline) or when the file has no
// embedded images. The stats ride the DMS indexingFinished event into
// File.metaData.docuaiImageStats; the trigger publishes a Rabbit re-index
// request via POST /documents/:id/ocr-images. Retire by deleting this file +
// its import/usage in document-info-general.tsx.
const DocumentImageStats = ({ document }) => {
  const classes = useStyles()
  const [requested, setRequested] = React.useState(false)

  const enabled = user?.experimentalFeatures?.includes(experimentalFeatures.useDocuAi)
  const stats = document?.metaData?.docuaiImageStats
  if (!enabled || !stats || !stats.count) {
    return null
  }

  const { count, large_count: largeCount, ocr_candidate_count: candidateCount, ocr } = stats
  const parts = [lang.documents.imageOcr.detected(count)]
  if (largeCount) {
    parts.push(lang.documents.imageOcr.large(largeCount))
  }
  parts.push(ocr?.performed
    ? lang.documents.imageOcr.ocrDone(ocr.images_ocred ?? 0, ocr.chars_added ?? 0)
    : lang.documents.imageOcr.ocrOff)

  // Pending if the user just clicked, or the backend recorded a request that has
  // not yet been answered by fresh stats (docuaiImageStats with an ocr block).
  const pending = requested || (!!document?.metaData?.docuaiOcrRequestedAt && !ocr?.performed)
  // Offer the trigger only when OCR hasn't run yet and there are candidates worth OCRing.
  const canTrigger = !ocr?.performed && (candidateCount ?? 0) > 0

  const triggerOcr = async() => {
    setRequested(true)
    try {
      await request(`${config.resourceHost}documents/${document.id}/ocr-images`, {
        auth: true,
        method: 'POST',
      })
      toast.showToast({ message: lang.documents.imageOcr.requestStarted, type: 'info' })
    } catch (error) {
      console.error('[imageOcr] trigger failed', error)
      setRequested(false)
      toast.showToast({ message: lang.documents.imageOcr.requestFailed, type: 'error' })
    }
  }

  return (
    <div className={classes.wrapper}>
      <label className={classes.label}>{lang.documents.imageOcr.title}</label>
      <div className={classes.value}>{parts.join(' · ')}</div>
      {canTrigger && (
        <div className={classes.action}>
          {pending
            ? <span className={classes.running}>{lang.documents.imageOcr.running}</span>
            : <button type='button' className={classes.button} onClick={triggerOcr} disabled={pending}>
              {lang.documents.imageOcr.runOcr}
            </button>}
        </div>
      )}
    </div>
  )
}

export default DocumentImageStats
